// src/components/reference/ReferenceGallery.tsx
/**
 * 設定資料ギャラリーコンポーネント
 * キャラクターの設定資料（三面図、衣装ラフなど）を表示
 * キャッシュ対応: orifoliCacheでフィードをキャッシュ
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, PhotoIcon, LightBulbIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { config } from '../../config';
import { auth } from '../../services/firebase';
import ReferenceUploadModal from './ReferenceUploadModal';
import ReferenceDetailModal from './ReferenceDetailModal';
import { orifoliCache, CachedFeedEntry } from '../../services/orifoliCache';

export interface ReferenceImage {
  modelId: string;
  title: string;
  description: string;
  creatorId: string;
  characterId: string | null;
  noise1Url: string;
  noise2Url: string;
  rid: string;
  thumbnailUrl: string | null;
  width: number;
  height: number;
  created: string;
  fallbackNoise1Url?: string;
  fallbackNoise2Url?: string;
  referenceType?: string;
}

interface ReferenceGalleryProps {
  characterId: string;
  characterName?: string;
  isOwner: boolean;
}

const ReferenceGallery: React.FC<ReferenceGalleryProps> = ({
  characterId,
  characterName,
  isOwner,
}) => {
  const { t } = useTranslation('character');

  // State
  const [references, setReferences] = useState<ReferenceImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReference, setSelectedReference] = useState<ReferenceImage | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const imageMetadataRef = useRef<Map<string, ReferenceImage>>(new Map());

  // キャッシュからフィードを復元
  const restoreFromCache = useCallback((): boolean => {
    const cachedData = orifoliCache.getFeedEntries('reference', characterId);
    if (!cachedData || cachedData.entries.length === 0) {
      return false;
    }

    // キャッシュからメタデータを復元
    const cachedImages: ReferenceImage[] = [];
    for (const entry of cachedData.entries) {
      const metadata = orifoliCache.getImageMetadata(entry.modelId) as ReferenceImage | null;
      if (metadata) {
        cachedImages.push(metadata);
        imageMetadataRef.current.set(entry.modelId, metadata);
      }
    }

    if (cachedImages.length > 0) {
      setReferences(cachedImages);
      console.log(`[ReferenceGallery] Restored ${cachedImages.length} items from cache`);
      return true;
    }
    return false;
  }, [characterId]);

  // キャッシュに保存
  const saveToCache = useCallback((items: ReferenceImage[]) => {
    if (items.length === 0) return;

    // フィードエントリを保存
    const entries: CachedFeedEntry[] = items.map(item => ({
      modelId: item.modelId,
      created: item.created,
    }));

    const firstTimestamp = items[0]?.created || null;
    const lastTimestamp = items[items.length - 1]?.created || null;

    orifoliCache.saveFeedEntries('reference', characterId, entries, firstTimestamp, lastTimestamp);

    // メタデータを保存
    items.forEach(item => {
      orifoliCache.saveImageMetadata(item.modelId, item as unknown as Record<string, unknown>);
      imageMetadataRef.current.set(item.modelId, item);
    });
  }, [characterId]);

  // Fetch reference images
  const fetchReferences = useCallback(async (forceRefresh = false) => {
    // キャッシュから復元を試みる（強制リフレッシュでない場合）
    if (!forceRefresh && restoreFromCache()) {
      setIsLoading(false);
      // バックグラウンドで新しいアイテムをチェック
      const cachedData = orifoliCache.getFeedEntries('reference', characterId);
      if (cachedData?.firstTimestamp) {
        try {
          const url = `${config.apiBaseUrl}/api/xor-pix/feed?character_id=${characterId}&reference_type=reference&limit=50`;
          let headers: HeadersInit = {};
          try {
            const user = auth.currentUser;
            if (user) {
              const token = await user.getIdToken();
              headers['Authorization'] = `Bearer ${token}`;
            }
          } catch {
            // 未ログインの場合はトークンなしでリクエスト
          }

          const response = await fetch(url, { method: 'GET', headers });
          if (response.ok) {
            const data = await response.json();
            const newItems = data.items as ReferenceImage[];
            if (newItems.length > 0 && newItems[0].created !== cachedData.firstTimestamp) {
              // 新しいアイテムがある場合は更新
              setReferences(newItems);
              saveToCache(newItems);
            }
          }
        } catch {
          // バックグラウンド更新のエラーは無視
        }
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `${config.apiBaseUrl}/api/xor-pix/feed?character_id=${characterId}&reference_type=reference&limit=50`;

      // 認証トークンを取得（ログイン中の場合）
      let headers: HeadersInit = {};
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch {
        // 未ログインの場合はトークンなしでリクエスト
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch references');
      }

      const data = await response.json();
      const items = data.items as ReferenceImage[];
      setReferences(items);
      saveToCache(items);
    } catch (err) {
      console.error('Error fetching references:', err);
      setError(t('gallery.loadError'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [characterId, t, restoreFromCache, saveToCache]);

  // リフレッシュ
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    orifoliCache.clearFeedCache('reference', characterId);
    fetchReferences(true);
  }, [characterId, fetchReferences]);

  useEffect(() => {
    fetchReferences();
  }, [characterId]); // fetchReferencesを依存配列から除外して無限ループを防ぐ

  // Handle upload complete
  const handleUploadComplete = () => {
    setShowUploadModal(false);
    // キャッシュをクリアしてリフレッシュ
    orifoliCache.clearFeedCache('reference', characterId);
    fetchReferences(true);
  };

  // Handle delete
  const handleDelete = async (modelId: string) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Delete via Firestore (soft delete)
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../services/firebase');

      await updateDoc(doc(db, 'models', modelId), {
        status: 'deleted',
        deletedAt: serverTimestamp()
      });

      // キャッシュから削除
      orifoliCache.removeFromFeed('reference', characterId, modelId);

      // ローカル状態を更新
      setReferences(prev => prev.filter(ref => ref.modelId !== modelId));
      setSelectedReference(null);
    } catch (err) {
      console.error('Error deleting reference:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-4">
        {/* 追加ボタン（オーナーのみ） */}
        {isOwner ? (
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            {t('settings.reference.add')}
          </button>
        ) : (
          <div />
        )}
        {/* リフレッシュボタン */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          title={t('actions.refresh')}
        >
          <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Empty state */}
      {references.length === 0 && (
        isOwner ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-10 text-center shadow-sm border border-gray-100 dark:border-gray-700">
            <LightBulbIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
              {t('settings.reference.emptyOwnerHint', { name: characterName || '' })}
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-5 py-2 mt-4 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              {t('settings.reference.add')}
            </button>
          </div>
        ) : (
          <div className="text-center py-12">
            <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              {t('settings.reference.empty')}
            </p>
          </div>
        )
      )}

      {/* Grid */}
      {references.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {references.map((ref) => (
            <button
              key={ref.modelId}
              onClick={() => setSelectedReference(ref)}
              className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow text-left"
            >
              {/* Thumbnail */}
              <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                {ref.thumbnailUrl ? (
                  <img
                    src={ref.thumbnailUrl}
                    alt={ref.title}
                    className="w-full h-full object-cover"
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PhotoIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>
              {/* Title */}
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {ref.title}
                </p>
                {ref.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                    {ref.description}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <ReferenceUploadModal
          characterId={characterId}
          onUploadComplete={handleUploadComplete}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {/* Detail Modal */}
      <ReferenceDetailModal
        isOpen={!!selectedReference}
        onClose={() => setSelectedReference(null)}
        reference={selectedReference}
        isOwner={isOwner}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default ReferenceGallery;
